import { useEffect, useMemo, useState } from "react";
import { TrendingUp, TrendingDown, AlertTriangle, Play, RotateCcw } from "lucide-react";
import { Aurora } from "@/components/Aurora";
import { FloorChart } from "@/components/funnel/FloorChart";

const MAX_YEARS = 8;
const BASE = 28;
const MOVES = {
  up: { label: "Market climbs", short: "Climbs", icon: TrendingUp, market: 2, iul: 1.4 },
  dip: { label: "Market dips", short: "Dips", icon: TrendingDown, market: -1.6, iul: 0 },
  crash: { label: "Market crashes", short: "Crashes", icon: AlertTriangle, market: -3.2, iul: 0 },
};
const CAPTIONS = {
  idle: { title: "Tap a market year below.", body: "Every tap adds a year. Watch how each path reacts." },
  up: { title: "The market climbed. So did your IUL.", body: "Growth is credited up to the policy's cap — you take part in the good years." },
  dip: { title: "The market fell. Your IUL held.", body: "The floor kicked in. Nothing you'd built went backward." },
  crash: { title: "The market crashed. Your IUL didn't move an inch.", body: "Every prior gain stays locked in. That's the whole point." },
  full: { title: "That's a full stretch.", body: "Notice the shape: one path zig-zags, the other only climbs or holds. Reset to run it again." },
};
const AUTOPLAY = ["up", "dip", "crash", "up", "up", "dip", "up", "up"];

const useSeries = (years) =>
  useMemo(() => {
    let m = 0;
    let u = 0;
    const pts = years.map((k) => {
      m += MOVES[k].market;
      u += MOVES[k].iul;
      return { key: k, market: m, iul: u };
    });
    const maxPos = Math.max(5, ...pts.map((p) => Math.max(p.market, p.iul)));
    const maxNeg = Math.max(2.5, ...pts.map((p) => -p.market));
    return { pts, unit: Math.min(66 / maxPos, (BASE - 3) / maxNeg) };
  }, [years]);

export const FloorExplainer = ({ product }) => {
  const [years, setYears] = useState([]);
  const [playing, setPlaying] = useState(false);
  const { pts, unit } = useSeries(years);
  const full = years.length >= MAX_YEARS;
  const last = years[years.length - 1];
  const caption = full ? CAPTIONS.full : last ? CAPTIONS[last] : CAPTIONS.idle;

  const add = (k) => setYears((y) => (y.length >= MAX_YEARS ? y : [...y, k]));
  const reset = () => {
    setPlaying(false);
    setYears([]);
  };
  const autoplay = () => {
    setYears([]);
    setPlaying(true);
  };

  useEffect(() => {
    if (!playing) return;
    if (years.length >= AUTOPLAY.length) {
      setPlaying(false);
      return;
    }
    const t = setTimeout(() => add(AUTOPLAY[years.length]), 750);
    return () => clearTimeout(t);
  }, [playing, years.length]);

  return (
    <section id="floor-explainer" className="relative bg-[#0B132B] text-white grain overflow-hidden scroll-mt-20">
      <Aurora intensity="default" />
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-14 md:py-28">
        <div className="max-w-3xl">
          <p data-reveal className="text-sm tracking-[0.2em] uppercase text-[#29ABE2] font-semibold mb-4">
            Try it yourself
          </p>
          <h2
            data-reveal
            className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]"
          >
            Two paths. <span className="text-gradient-breeze">One never goes backward.</span>
          </h2>
          <p data-reveal className="mt-5 text-base md:text-lg text-white/70 font-light leading-relaxed">
            Pick what the market does each year. The market path can rise or fall. Your {product.tag} path
            can only rise or hold. This is a concept, not a projection — no figures, just the shape.
          </p>
        </div>

        <div
          data-reveal
          data-testid="floor-explainer-card"
          className="mt-10 md:mt-14 rounded-[2rem] bg-white/[0.04] backdrop-blur-xl border border-white/10 p-5 sm:p-8 md:p-10 shadow-2xl shadow-black/30"
        >
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-semibold">
            <span className="inline-flex items-center gap-2 text-white/70">
              <span className="w-3 h-3 rounded-sm bg-white/35" /> Market path
            </span>
            <span className="inline-flex items-center gap-2 text-[#4CC3F0]">
              <span className="w-3 h-3 rounded-sm bg-breeze-gradient" /> Your {product.tag} path
            </span>
            <span className="inline-flex items-center gap-2 text-white/40">
              <span className="w-3 h-px bg-[#29ABE2]" /> Below the line = money lost
            </span>
          </div>

          <div className="relative mt-6 rounded-2xl bg-[#060b1a]/70 ring-1 ring-white/10 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/8 text-[11px] sm:text-xs font-bold tracking-[0.18em] uppercase">
              <span className="inline-flex items-center gap-2 text-white/60">
                <span className="w-1.5 h-1.5 rounded-full bg-[#4CC3F0] animate-pulse" /> {product.tag} vs. Market · Live
              </span>
              <span className="text-white/35 hidden sm:inline">Concept · Not a projection</span>
            </div>
            <div className="relative h-64 sm:h-80 md:h-96 mx-2 sm:mx-4 mt-3 mb-12 sm:mb-14" data-testid="floor-chart">
              <FloorChart pts={pts} unit={unit} base={BASE} maxYears={MAX_YEARS} moves={MOVES} tag={product.tag} />
              <span
                className="absolute right-0 translate-y-full pt-1.5 text-[11px] sm:text-sm font-bold tracking-[0.15em] uppercase text-[#29ABE2] pointer-events-none"
                style={{ bottom: `${BASE}%` }}
              >
                Protected floor
              </span>
              {years.length === 0 && (
                <p
                  data-testid="floor-chart-empty"
                  style={{ bottom: `${BASE}%` }}
                  className="absolute inset-x-0 top-0 flex items-center justify-center text-center px-6 text-white/45 font-light text-base sm:text-lg pointer-events-none"
                >
                  Your first year starts here ↓
                </p>
              )}
              {pts.map((p, i) => {
                const Icon = MOVES[p.key].icon;
                const locked = MOVES[p.key].iul === 0;
                return (
                  <span
                    key={i}
                    className={`absolute -bottom-8 sm:-bottom-9 -translate-x-1/2 ${locked ? "text-white/45" : "text-[#4CC3F0]"}`}
                    style={{ left: `${((i + 1) / MAX_YEARS) * 100}%`, animation: "iul-fade 0.4s ease-out both" }}
                  >
                    <Icon size={15} />
                  </span>
                );
              })}
            </div>
          </div>

          <div
            key={caption.title}
            data-testid="floor-caption"
            className="mt-5 rounded-2xl bg-[#29ABE2]/10 border border-[#29ABE2]/25 px-5 py-4 sm:px-6 sm:py-5"
            style={{ animation: "iul-fade 0.45s ease-out both" }}
          >
            <p className="font-heading font-bold text-lg sm:text-xl tracking-tight">{caption.title}</p>
            <p className="mt-1 text-sm sm:text-base text-white/70 font-light">{caption.body}</p>
          </div>

          <div className="mt-5 grid grid-cols-3 gap-2 sm:gap-3">
            {Object.entries(MOVES).map(([k, m]) => {
              const Icon = m.icon;
              const danger = m.iul === 0;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => add(k)}
                  disabled={full || playing}
                  data-testid={`floor-btn-${k}`}
                  className={`group inline-flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-2.5 rounded-2xl sm:rounded-full px-3 py-3.5 sm:px-6 sm:py-4 font-semibold text-sm sm:text-base border transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 ${
                    danger
                      ? "bg-white/5 border-white/15 text-white hover:border-white/40 hover:bg-white/10"
                      : "bg-[#29ABE2] border-[#29ABE2] text-white hover:bg-white hover:text-[#0B132B]"
                  }`}
                >
                  <Icon size={18} />
                  <span className="sm:hidden">{m.short}</span>
                  <span className="hidden sm:inline">{m.label}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-2 sm:mt-3 grid grid-cols-[2fr_1fr] gap-2 sm:gap-3">
            <button
              type="button"
              onClick={autoplay}
              disabled={playing}
              data-testid="floor-autoplay-btn"
              className="inline-flex items-center justify-center gap-2 sm:gap-2.5 rounded-2xl sm:rounded-full px-3 py-3.5 sm:px-6 sm:py-4 font-semibold text-sm sm:text-base border border-[#4CC3F0]/40 bg-[#4CC3F0]/10 text-[#4CC3F0] transition-all duration-300 hover:bg-[#4CC3F0] hover:text-[#0B132B] disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
            >
              <Play size={18} />
              <span className="sm:hidden">{playing ? "Playing…" : "Auto-play"}</span>
              <span className="hidden sm:inline">{playing ? "Playing a sample stretch…" : "Auto-play a sample stretch"}</span>
            </button>
            <button
              type="button"
              onClick={reset}
              data-testid="floor-reset-btn"
              className="inline-flex items-center justify-center gap-2 sm:gap-2.5 rounded-2xl sm:rounded-full px-3 py-3.5 sm:px-6 sm:py-4 font-semibold text-sm sm:text-base border border-white/15 bg-white/5 text-white/80 transition-all duration-300 hover:border-white/40 hover:bg-white/10 hover:text-white active:scale-95"
            >
              <RotateCcw size={18} /> Reset
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
